// Price-anchoring step: Calofay annual per-day cost vs. a 1 SAR water bottle.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Camera, Brain, ScanLine, ChefHat, LineChart, Dumbbell, Users, Check } from "lucide-react";
import api from "@/lib/api";

const SAR_ANNUAL = 247;
const CUR_AR = { SAR: "ريال", AED: "درهم", QAR: "ريال", KWD: "دينار", BHD: "دينار", OMR: "ريال", USD: "$", EUR: "€" };

const COPY = {
  ar: {
    kicker: "مقارنة الأسعار",
    headline: "قارن سعرنا بقارورة ماء",
    water: "قارورة ماء",
    calofay: "كالوفاي يومياً",
    perDay: "باليوم",
    annualNote: (p, c) => `بالاشتراك السنوي (${p} ${c})`,
    verdict: "صحتك كل يوم بأقل من سعر قارورة ماء",
    featuresTitle: "وش تحصل مقابل هالسعر؟",
    features: [
      [Camera, "صوّر أكلك واعرف سعراته فوراً"],
      [Brain, "مدرب ذكي يرد عليك ٢٤ ساعة"],
      [ScanLine, "قراءة الملصق الغذائي بالكاميرا"],
      [ChefHat, "وصفات صحية + استيراد وصفات انستقرام"],
      [LineChart, "متابعة الوزن والتقدم بالرسوم"],
      [Dumbbell, "حساب سعرات التمارين"],
      [Users, "شارك تقدمك مع مدربك أو أخصائيتك"],
    ],
    cta: "متابعة",
  },
  en: {
    kicker: "Price check",
    headline: "Compare our price to a bottle of water",
    water: "Bottle of water",
    calofay: "Calofay per day",
    perDay: "/ day",
    annualNote: (p, c) => `on the annual plan (${p} ${c})`,
    verdict: "Your health every day for less than a bottle of water",
    featuresTitle: "What you get for that price",
    features: [
      [Camera, "Snap your food, get calories instantly"],
      [Brain, "AI coach that answers 24/7"],
      [ScanLine, "Scan nutrition labels with your camera"],
      [ChefHat, "Healthy recipes + Instagram recipe import"],
      [LineChart, "Weight & progress tracking charts"],
      [Dumbbell, "Exercise calorie tracking"],
      [Users, "Share progress with your coach or dietitian"],
    ],
    cta: "Continue",
  },
};

const fmt = (n) => (n >= 10 ? n.toFixed(0) : n.toFixed(2).replace(/\.?0+$/, ""));

function usePrices() {
  const [p, setP] = useState({ currency: "SAR", annual: SAR_ANNUAL });
  useEffect(() => {
    api.get("/payments/pricing").then(({ data }) => {
      const a = data?.plans?.find((x) => x.key === "annual");
      if (a?.price) setP({ currency: data.currency, annual: a.price });
    }).catch(() => {});
  }, []);
  const water = p.annual / SAR_ANNUAL; // 1 SAR equivalent in local currency
  return { ...p, water, daily: p.annual / 365 };
}

function PriceCard({ children, label, price, unit, highlight, testid }) {
  return (
    <div
      className={`flex-1 rounded-3xl p-3 pt-4 flex flex-col items-center text-center ${highlight ? "bg-white border-2 border-brand-500 shadow-[0_18px_40px_-18px_rgba(249,115,22,0.55)]" : "bg-white border border-sky-100"}`}
      data-testid={testid}
    >
      <div className="h-32 flex items-center justify-center">{children}</div>
      <div className="text-xs font-bold text-neutral-500 mt-2">{label}</div>
      <div className={`mt-1 font-black num ${highlight ? "text-brand-600 text-3xl" : "text-sky-700 text-2xl"}`} dir="ltr" data-testid={`${testid}-price`}>{price}</div>
      <div className="text-[11px] font-bold text-neutral-400">{unit}</div>
    </div>
  );
}

export default function OnboardingWaterCompareScreen({ lang = "ar", onContinue }) {
  const isRTL = lang !== "en";
  const t = COPY[isRTL ? "ar" : "en"];
  const { currency, annual, water, daily } = usePrices();
  const cur = isRTL ? (CUR_AR[currency] || currency) : currency;

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="always-light fixed inset-0 z-[90] bg-white flex flex-col overflow-hidden" data-testid="onboarding-water-compare">
      <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(70% 45% at 50% 10%, rgba(254,215,170,0.55) 0%, rgba(255,255,255,0) 65%), linear-gradient(180deg,#FFF7ED 0%,#FFFFFF 50%)" }} />
      <div className="relative z-10 flex-1 overflow-y-auto" style={{ paddingTop: "calc(env(safe-area-inset-top) + 20px)" }}>
        <div className="max-w-md mx-auto px-5 pb-40">
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center">
            <span className="orange-gradient text-white text-[11px] font-black tracking-widest px-3 py-1.5 rounded-full">{t.kicker}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-2xl sm:text-3xl font-black text-neutral-900 text-center leading-tight mt-3" data-testid="water-compare-headline">
            {t.headline}
          </motion.h1>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="relative flex items-stretch gap-3 mt-6">
            <PriceCard label={t.water} price={fmt(water)} unit={cur} testid="water-card">
              <img src="/water-bottle.jpg" alt="" className="h-32 w-auto rounded-2xl" />
            </PriceCard>
            <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-neutral-900 text-white text-xs font-black flex items-center justify-center ring-4 ring-white" data-testid="water-compare-vs">VS</div>
            <PriceCard label={t.calofay} price={fmt(daily)} unit={`${cur} ${t.perDay}`} highlight testid="calofay-card">
              <img src="/apple-touch-icon.png" alt="Calofay" className="w-24 h-24 rounded-[1.6rem] shadow-lg" />
            </PriceCard>
          </motion.div>
          <p className="text-[11px] text-neutral-400 text-center mt-2 num" data-testid="water-compare-annual-note">{t.annualNote(fmt(annual), cur)}</p>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35 }} className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-700 font-black text-sm text-center py-3 px-4" data-testid="water-compare-verdict">
            {t.verdict}
          </motion.div>

          <div className="mt-6">
            <div className="text-sm font-black text-neutral-900 mb-3">{t.featuresTitle}</div>
            <ul className="space-y-2.5" data-testid="water-compare-features">
              {t.features.map(([Icon, text], i) => (
                <motion.li key={text} initial={{ opacity: 0, x: isRTL ? 12 : -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 + i * 0.06 }} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4" /></span>
                  <span className="flex-1 text-sm font-bold text-neutral-700">{text}</span>
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)", paddingTop: "26px", background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 40%, #fff 100%)" }}>
        <div className="max-w-md mx-auto px-5 pointer-events-auto">
          <button type="button" onClick={onContinue} className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2" data-testid="onboarding-water-continue">
            {t.cta}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
